import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect } from 'react';
import { Dialog, Box, IconButton, useTheme } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { DismissRegular } from '@fluentui/react-icons';
const LiveImagePreviewDialog = ({ onClose, open, imgSrc, alt }) => {
    const theme = useTheme();
    const containerRef = useRef(null);
    const isDarkMode = theme.palette.mode === 'dark';
    const [scale, setScale] = useState(1);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);
    const MIN_SCALE = 1;
    const MAX_SCALE = 10;
    useEffect(() => {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
        setImageLoaded(false); // reset when imgSrc changes
    }, [imgSrc]);
    const handleWheel = (e) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container)
            return;
        const { left, top } = container.getBoundingClientRect();
        const mouseX = e.clientX - left;
        const mouseY = e.clientY - top;
        const delta = e.deltaY > 0 ? -0.3 : 0.3;
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale + delta));
        if (newScale === scale)
            return;
        const scaleRatio = newScale / scale;
        const originX = mouseX - translate.x;
        const originY = mouseY - translate.y;
        setScale(newScale);
        setTranslate({
            x: translate.x - originX * (scaleRatio - 1),
            y: translate.y - originY * (scaleRatio - 1)
        });
    };
    const handleZoom = (delta, clientX, clientY) => {
        const container = containerRef.current;
        if (!container)
            return;
        const { left, top } = container.getBoundingClientRect();
        const mouseX = clientX - left;
        const mouseY = clientY - top;
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale + delta));
        if (newScale === scale)
            return;
        const scaleRatio = newScale / scale;
        const originX = mouseX - translate.x;
        const originY = mouseY - translate.y;
        setScale(newScale);
        setTranslate({
            x: translate.x - originX * (scaleRatio - 1),
            y: translate.y - originY * (scaleRatio - 1)
        });
    };
    const handleZoomIn = () => {
        const container = containerRef.current;
        if (!container)
            return;
        handleZoom(0.3, container.clientWidth / 2, container.clientHeight / 2);
    };
    const handleZoomOut = () => {
        const container = containerRef.current;
        if (!container)
            return;
        handleZoom(-0.3, container.clientWidth / 2, container.clientHeight / 2);
    };
    return (_jsx(Dialog, { onClose: onClose, open: open, maxWidth: 'md', fullWidth: true, PaperProps: {
            sx: {
                height: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
            }
        }, children: _jsxs(Box, { sx: {
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2
            }, children: [_jsx(IconButton, { size: 'small', onClick: onClose, sx: {
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        backgroundColor: theme.palette.background.paper,
                        color: isDarkMode ? 'white' : theme.palette.text.primary,
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover
                        }
                    }, "aria-label": 'Close', children: _jsx(DismissRegular, { fontSize: 20, color: "inherit" }) }), _jsx(Box, { ref: containerRef, onWheel: handleWheel, sx: {
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                        cursor: scale > 1 ? 'grab' : 'default',
                        position: 'relative'
                    }, children: _jsx(Box, { sx: {
                            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                            transformOrigin: '0 0',
                            transition: 'transform 0.1s ease-out',
                            width: '100%',
                            height: '100%',
                            position: 'relative'
                        }, children: _jsx("img", { src: imgSrc, alt: alt, onLoad: () => setImageLoaded(true), style: {
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                display: 'block',
                                pointerEvents: 'none',
                                opacity: imageLoaded ? 1 : 0,
                                transition: 'opacity 0.5s ease-in-out'
                            } }, imgSrc) }) }), _jsxs(Box, { sx: {
                        position: 'absolute',
                        bottom: 16,
                        right: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        borderRadius: 1,
                        p: 0.5
                    }, children: [_jsx(IconButton, { size: 'small', onClick: handleZoomIn, sx: { color: 'white' }, "aria-label": 'Zoom In', children: _jsx(Add, { fontSize: 'small' }) }), _jsx(IconButton, { size: 'small', onClick: handleZoomOut, sx: { color: 'white' }, "aria-label": 'Zoom Out', children: _jsx(Remove, { fontSize: 'small' }) })] })] }) }));
};
export default LiveImagePreviewDialog;
